/**
 * Firebase initialization helpers, analytics tracker, and anonymous authentication bootstrapper.
 */
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import {
  getAnalytics,
  isSupported,
  logEvent as gaLogEvent,
  setDefaultEventParameters,
  setUserId,
} from 'firebase/analytics';

const firebaseConfig = {
  apiKey: "AIzaSyCu4Bnb6SP3RIANclnPYLK7gc1iJtV0DLQ",
  authDomain: "smallsteps-addup.firebaseapp.com",
  projectId: "smallsteps-addup",
  storageBucket: "smallsteps-addup.firebasestorage.app",
  messagingSenderId: "295186687268",
  appId: "1:295186687268:web:038bb42be0cbae7efd7fb1",
  measurementId: "G-C6KPGR8QBE"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

/**
 * Determines whether analytics should be disabled for the current host.
 */
function isLocalhost() {
  if (typeof window === 'undefined') {
    return false;
  }

  const { hostname } = window.location;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
}

let analyticsInstance;

/**
 * Lazily initializes Firebase Analytics when supported by the environment.
 */
async function initAnalytics() {
  if (isLocalhost()) {
    return null;
  }

  if (!analyticsInstance && (await isSupported())) {
    analyticsInstance = getAnalytics(app);
    setDefaultEventParameters(analyticsInstance, { app_name: 'LocalVoice' });
    if (auth.currentUser?.uid) {
      setUserId(analyticsInstance, auth.currentUser.uid);
    }
  }

  return analyticsInstance;
}

/**
 * Logs an analytics event when analytics is available.
 */
export async function track(eventName, params = {}) {
  const analytics = await initAnalytics().catch(() => null);
  if (analytics) {
    gaLogEvent(analytics, eventName, params);
  }
}

/**
 * Ensures a visitor is logged in with an anonymous Firebase user.
 */
export function ensureAnonymousUser() {
  if (auth.currentUser) {
    initAnalytics().catch(() => {});
    return Promise.resolve(auth.currentUser);
  }

  return new Promise((resolve, reject) => {
    const stopListening = onAuthStateChanged(
      auth,
      (user) => {
        if (user) {
          stopListening();
          initAnalytics()
            .then((analytics) => {
              if (analytics) {
                setUserId(analytics, user.uid);
              }
            })
            .catch(() => {});
          resolve(user);
          return;
        }

        signInAnonymously(auth).catch((error) => {
          stopListening();
          reject(error);
        });
      },
      (error) => {
        stopListening();
        reject(error);
      },
    );
  }).catch((error) => {
    console.error('Failed to ensure anonymous Firebase authentication.', error);
    throw error;
  });
}

export { app, auth };
